import logger from '../utils/logger';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { GraduationCap, Plus, BookOpen, Award, CheckCircle2, AlertTriangle } from 'lucide-react';

const CAT_COLORS = {
    general: 'bg-slate-100 text-slate-700',
    compliance: 'bg-red-100 text-red-700',
    sales: 'bg-emerald-100 text-emerald-700',
    technical: 'bg-blue-100 text-blue-700',
    leadership: 'bg-purple-100 text-purple-700',
};

const Learning = () => {
    const { canManage, user } = useAuth();
    const [tab, setTab] = useState('catalog');
    const [courses, setCourses] = useState([]);
    const [myCourses, setMyCourses] = useState([]);
    const [createOpen, setCreateOpen] = useState(false);
    const [form, setForm] = useState({ title: '', description: '', category: 'general', is_mandatory: false, pass_score_pct: 70, modules: [] });
    const [modDraft, setModDraft] = useState({ title: '', duration_minutes: 10 });

    const fetchAll = useCallback(async () => {
        try {
            const [c, mc] = await Promise.all([
                axios.get(`${API_URL}/api/learning/courses`, { withCredentials: true }),
                axios.get(`${API_URL}/api/learning/my-courses`, { withCredentials: true }),
            ]);
            setCourses(c.data || []);
            setMyCourses(mc.data || []);
        } catch (e) { logger.error('learning fetch', e); }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const submitCourse = async () => {
        if (!form.title || !form.description) { alert('Title + description required'); return; }
        try {
            await axios.post(`${API_URL}/api/learning/courses`, form, { withCredentials: true });
            setForm({ title: '', description: '', category: 'general', is_mandatory: false, pass_score_pct: 70, modules: [] });
            setCreateOpen(false);
            fetchAll();
        } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    };

    const enroll = async (course_id) => {
        try {
            await axios.post(`${API_URL}/api/learning/enrollments`, { course_id }, { withCredentials: true });
            fetchAll();
            alert('Enrolled! Find it in "My Courses"');
        } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    };

    const completeCourse = async (enrollment_id) => {
        const score = parseInt(prompt('Enter your score % (0-100):') || '0');
        if (isNaN(score) || score < 0 || score > 100) return;
        try {
            const { data } = await axios.post(`${API_URL}/api/learning/enrollments/${enrollment_id}/complete`, { score_pct: score }, { withCredentials: true });
            fetchAll();
            alert(data.passed ? `🎓 Congrats! Certificate ${data.certificate_id} issued.` : 'You did not pass — try again after review.');
        } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    };

    const enrolledIds = new Set(myCourses.map(mc => mc.course_id));
    const completed = myCourses.filter(mc => mc.status === 'completed').length;

    return (
        <div className="space-y-6" data-testid="learning-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Learning Center</h1>
                    <p className="text-slate-500 mt-1">Micro-courses, mandatory compliance, certificates.</p>
                </div>
                {canManage && (
                    <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm" data-testid="new-course-btn"><Plus className="h-4 w-4 mr-2" />New Course</Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-2xl max-w-xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Create Course</DialogTitle><DialogDescription>Define a new learning course with modules and pass score.</DialogDescription></DialogHeader>
                            <div className="space-y-3 mt-3">
                                <div><Label className="text-xs font-bold uppercase text-slate-500">Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="rounded-lg mt-1 bg-slate-50 border-slate-200" data-testid="course-title" /></div>
                                <div><Label className="text-xs font-bold uppercase text-slate-500">Description *</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} className="rounded-lg mt-1 bg-slate-50 border-slate-200" /></div>
                                <div className="grid grid-cols-3 gap-3">
                                    <div><Label className="text-xs font-bold uppercase text-slate-500">Category</Label>
                                        <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm mt-1">
                                            {['general', 'compliance', 'sales', 'technical', 'leadership'].map(c => <option key={c} value={c}>{c}</option>)}
                                        </select>
                                    </div>
                                    <div><Label className="text-xs font-bold uppercase text-slate-500">Pass %</Label><Input type="number" value={form.pass_score_pct} onChange={e => setForm({ ...form, pass_score_pct: parseInt(e.target.value) })} className="rounded-lg mt-1 bg-slate-50 border-slate-200" /></div>
                                    <div className="flex items-end pb-1.5"><label className="text-xs flex items-center gap-2"><input type="checkbox" checked={form.is_mandatory} onChange={e => setForm({ ...form, is_mandatory: e.target.checked })} />Mandatory</label></div>
                                </div>
                                <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
                                    <Label className="text-xs font-bold uppercase text-slate-500">Modules</Label>
                                    {form.modules.map((m, i) => (
                                        <div key={`${m.title}-${i}`} className="text-xs flex justify-between bg-white border border-slate-200 rounded-lg px-3 py-2 mt-2">
                                            <span><strong>{i + 1}. {m.title}</strong> · {m.duration_minutes} min</span>
                                            <button onClick={() => setForm({ ...form, modules: form.modules.filter((_, j) => j !== i) })} className="text-red-500">×</button>
                                        </div>
                                    ))}
                                    <div className="flex gap-2 mt-2">
                                        <Input placeholder="Module title" value={modDraft.title} onChange={e => setModDraft({ ...modDraft, title: e.target.value })} className="flex-1 rounded-lg bg-white text-xs" />
                                        <Input type="number" placeholder="min" value={modDraft.duration_minutes} onChange={e => setModDraft({ ...modDraft, duration_minutes: parseInt(e.target.value) })} className="w-20 rounded-lg bg-white text-xs" />
                                        <Button variant="outline" className="rounded-lg" onClick={() => { if (modDraft.title) { setForm({ ...form, modules: [...form.modules, { ...modDraft, order: form.modules.length + 1 }] }); setModDraft({ title: '', duration_minutes: 10 }); } }}>+</Button>
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2"><Button variant="outline" className="rounded-lg" onClick={() => setCreateOpen(false)}>Cancel</Button><Button onClick={submitCourse} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm" data-testid="course-save-btn">Create</Button></div>
                            </div>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                    { label: 'Total Courses', value: courses.length, color: 'border-t-[#2563eb]', icon: BookOpen },
                    { label: 'Mandatory', value: courses.filter(c => c.is_mandatory).length, color: 'border-t-red-500', icon: AlertTriangle },
                    { label: 'My Enrollments', value: myCourses.length, color: 'border-t-amber-500', icon: GraduationCap },
                    { label: 'My Completed', value: completed, color: 'border-t-emerald-500', icon: Award },
                ].map(m => (
                    <Card key={m.label} className={`bg-white border border-slate-200 rounded-xl shadow-sm border-t-4 ${m.color} overflow-hidden`}>
                        <CardContent className="p-5 flex items-start justify-between">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{m.label}</p>
                                <p className="text-2xl font-extrabold text-slate-900 mt-2 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>{m.value}</p>
                            </div>
                            <m.icon className="h-5 w-5 text-slate-400" />
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Tabs */}
            <div className="flex gap-2 bg-white border border-slate-200 rounded-xl p-1.5 shadow-sm w-fit">
                {[{ k: 'catalog', label: 'Course Catalog' }, { k: 'my', label: `My Courses (${myCourses.length})` }].map(t => (
                    <Button key={t.k} variant={tab === t.k ? 'default' : 'ghost'} size="sm" className={tab === t.k ? 'bg-[#2563eb] text-white rounded-lg' : 'rounded-lg'} onClick={() => setTab(t.k)} data-testid={`learning-tab-${t.k}`}>{t.label}</Button>
                ))}
            </div>

            {tab === 'catalog' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {courses.length === 0 && <Card className="rounded-xl shadow-sm col-span-full"><CardContent className="p-12 text-center text-slate-400">No courses yet.</CardContent></Card>}
                    {courses.map(c => (
                        <Card key={c._id} className="rounded-xl shadow-sm border-slate-200 hover:shadow-md transition-shadow overflow-hidden" data-testid={`course-${c._id}`}>
                            <CardContent className="p-5">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${CAT_COLORS[c.category] || 'bg-slate-100 text-slate-600'}`}>{c.category}</span>
                                    {c.is_mandatory && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-100 text-red-700">Mandatory</span>}
                                </div>
                                <h3 className="font-bold text-slate-900">{c.title}</h3>
                                <p className="text-xs text-slate-500 mt-1 line-clamp-3">{c.description}</p>
                                <p className="text-[10px] text-slate-400 mt-2">{c.modules?.length || 0} modules · pass {c.pass_score_pct}%</p>
                                <Button onClick={() => enroll(c._id)} disabled={enrolledIds.has(c._id)} size="sm" variant={enrolledIds.has(c._id) ? 'outline' : 'default'} className={`rounded-lg w-full mt-3 ${!enrolledIds.has(c._id) ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-sm' : ''}`} data-testid={`enroll-${c._id}`}>
                                    {enrolledIds.has(c._id) ? 'Enrolled ✓' : 'Enroll'}
                                </Button>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {tab === 'my' && (
                <div className="space-y-3">
                    {myCourses.length === 0 && <Card className="rounded-xl shadow-sm"><CardContent className="p-12 text-center text-slate-400">You're not enrolled in any course. Browse the catalog!</CardContent></Card>}
                    {myCourses.map(mc => (
                        <Card key={mc._id} className="rounded-xl shadow-sm border-slate-200 overflow-hidden" data-testid={`my-course-${mc._id}`}>
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex-1">
                                        <h3 className="font-bold text-slate-900">{mc.course_title}</h3>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${mc.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : mc.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{mc.status}</span>
                                            {mc.score_pct !== null && <span className="text-xs text-slate-500">Score: {mc.score_pct}%</span>}
                                            {mc.certificate_id && <span className="text-xs font-mono text-emerald-600">🏆 {mc.certificate_id}</span>}
                                        </div>
                                    </div>
                                    {mc.status === 'in_progress' && <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm" onClick={() => completeCourse(mc._id)} data-testid={`complete-${mc._id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Mark Complete</Button>}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Learning;
